import type { WallSegment } from '@manudev/model';
import type { Point2 } from './frame';

// The outline a storey's walls enclose, as the convex hull of their centreline endpoints.
// Centrelines, not outer faces: a hull over the axes is the one shape that stays a simple
// closed polygon whatever the wall graph looks like, and the Duplex's outer walls are flush.
export function outerFootprint(segments: WallSegment[]): Point2[] {
  const points: Point2[] = [];
  for (const s of segments) points.push(s.start, s.end);
  return convexHull(points);
}

export function convexHull(points: Point2[]): Point2[] {
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const unique = sorted.filter(
    (p, i) => i === 0 || p[0] !== sorted[i - 1]![0] || p[1] !== sorted[i - 1]![1],
  );
  if (unique.length < 3) return unique;
  const half = (input: Point2[]): Point2[] => {
    const out: Point2[] = [];
    for (const p of input) {
      while (out.length >= 2 && cross(out[out.length - 2]!, out[out.length - 1]!, p) <= 0)
        out.pop();
      out.push(p);
    }
    out.pop();
    return out;
  };
  return [...half(unique), ...half([...unique].reverse())];
}

export function polygonContains(polygon: Point2[], p: Point2): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]!;
    const b = polygon[j]!;
    const straddles = a[1] > p[1] !== b[1] > p[1];
    if (straddles && p[0] < ((b[0] - a[0]) * (p[1] - a[1])) / (b[1] - a[1]) + a[0])
      inside = !inside;
  }
  return inside;
}

const cross = (o: Point2, a: Point2, b: Point2) =>
  (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
